<template>
  <HrPageLayout
    title="Off Days"
    subtitle="Create off-day programs and assign to departments or employees"
    icon="🏖️"
    :breadcrumb-items="[{ label: 'Leave Management' }, { label: 'Off Days' }]"
  >
    <template #header-actions>
      <select v-model.number="selectedYear" class="hr-select" style="width: 120px;" @change="loadPrograms">
        <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
      </select>
      <button class="hr-btn hr-btn-primary" @click="openForm()">
        <i class="bx bx-plus-circle"></i> New Off Day Program
      </button>
    </template>

    <div class="hr-chart-card">
      <div class="hr-chart-header">
        <h5 class="hr-chart-title"><i class="bx bx-calendar-star"></i> Off Day Programs</h5>
        <div class="hr-search-wrap" style="min-width: 240px;">
          <input v-model="search" type="text" class="hr-input" placeholder="Search programs..." @keyup.enter="loadPrograms">
          <i class="bx bx-search-alt search-icon"></i>
        </div>
      </div>
      <div class="hr-chart-body">
        <div v-if="loading" class="hr-loading-state">
          <div class="hr-spinner"></div>
          <p>Loading...</p>
        </div>
        <div v-else-if="!programs.length" class="hr-empty-state">
          <i class="bx bx-calendar-x"></i>
          <h5>No off day programs</h5>
          <p>Create a program and assign departments or employees.</p>
          <button class="hr-btn hr-btn-primary" @click="openForm()"><i class="bx bx-plus"></i> Create Program</button>
        </div>
        <div v-else class="hr-table-wrapper">
          <table class="hr-table">
            <thead>
              <tr>
                <th>Program</th>
                <th>Year</th>
                <th>Max days / year</th>
                <th>Assignments</th>
                <th>Pending</th>
                <th>Status</th>
                <th style="text-align:center;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in programs" :key="p.id">
                <td>
                  <strong>{{ p.program_name }}</strong>
                  <br><small class="text-muted">{{ p.description || '—' }}</small>
                </td>
                <td>{{ p.year }}</td>
                <td>{{ p.max_days_per_year }}</td>
                <td>
                  <span class="hr-badge hr-badge-muted">{{ p.dept_assign_count || 0 }} dept(s)</span>
                  <span class="hr-badge hr-badge-info">{{ p.emp_assign_count || 0 }} employee(s)</span>
                </td>
                <td>{{ p.pending_requests || 0 }}</td>
                <td>
                  <span class="hr-badge" :class="p.is_active == 1 ? 'hr-badge-success' : 'hr-badge-muted'">
                    {{ p.is_active == 1 ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td style="text-align:center;">
                  <button class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" title="Edit" @click="openForm(p)"><i class="bx bx-edit"></i></button>
                  <button class="hr-btn hr-btn-warning hr-btn-sm hr-btn-icon" title="Toggle" @click="toggleProgram(p)"><i class="bx bx-power-off"></i></button>
                  <button class="hr-btn hr-btn-danger hr-btn-sm hr-btn-icon" title="Delete" @click="deleteProgram(p)"><i class="bx bx-trash"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showForm" class="modal fade show d-block" tabindex="-1" style="background:rgba(0,0,0,.45);">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ form.id ? 'Edit' : 'Create' }} Off Day Program</h5>
            <button type="button" class="btn-close" @click="closeForm"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-8">
                <label class="form-label">Program name *</label>
                <input v-model="form.programName" class="form-control" placeholder="e.g. Team building off day">
              </div>
              <div class="col-md-4">
                <label class="form-label">Year *</label>
                <select v-model.number="form.year" class="form-select">
                  <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-control" rows="2"></textarea>
              </div>
              <div class="col-md-4">
                <label class="form-label">Max days per employee / year *</label>
                <input v-model.number="form.maxDaysPerYear" type="number" min="0.5" step="0.5" class="form-control">
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <div class="form-check">
                  <input id="allowHalf" v-model="form.allowHalfDay" class="form-check-input" type="checkbox">
                  <label class="form-check-label" for="allowHalf">Allow half day</label>
                </div>
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <div class="form-check">
                  <input id="isActive" v-model="form.isActive" class="form-check-input" type="checkbox">
                  <label class="form-check-label" for="isActive">Active</label>
                </div>
              </div>
            </div>

            <hr>
            <h6 class="mb-2">Assign to departments</h6>
            <div v-if="!departments.length" class="text-muted small">Loading departments...</div>
            <div v-else class="row g-2">
              <div v-for="d in departments" :key="d.id" class="col-md-4 col-6">
                <label class="form-check">
                  <input v-model="form.departmentIds" class="form-check-input" type="checkbox" :value="d.id">
                  <span class="form-check-label">{{ d.departmentName }}</span>
                </label>
              </div>
            </div>

            <hr>
            <h6 class="mb-2">Assign to specific employees</h6>
            <div class="row g-2 mb-2">
              <div class="col-md-6">
                <input v-model="employeeSearch" class="form-control" placeholder="Search by name or ID..." @keyup.enter="searchEmployees">
              </div>
              <div class="col-md-3">
                <button type="button" class="btn btn-outline-primary w-100" @click="searchEmployees">Search</button>
              </div>
            </div>
            <div v-if="employeeResults.length" class="border rounded p-2 mb-2" style="max-height:160px;overflow:auto;">
              <button
                v-for="e in employeeResults"
                :key="e.nationalId"
                type="button"
                class="btn btn-sm btn-outline-secondary me-1 mb-1"
                @click="addEmployee(e)"
              >
                + {{ e.fullName || (e.firstName + ' ' + e.lastName) }} ({{ e.nationalId }})
              </button>
            </div>
            <div v-if="selectedEmployees.length" class="d-flex flex-wrap gap-1">
              <span v-for="e in selectedEmployees" :key="e.nationalId" class="badge bg-primary">
                {{ e.label }}
                <button type="button" class="btn-close btn-close-white ms-1" style="font-size:.5rem;" @click="removeEmployee(e.nationalId)"></button>
              </span>
            </div>
            <p class="text-muted small mt-2 mb-0">Select at least one department or employee. Assigned staff will see this program in self-service.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeForm">Cancel</button>
            <button type="button" class="btn btn-primary" :disabled="saving" @click="saveProgram">
              {{ saving ? 'Saving...' : 'Save Program' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from '@/components/hrActions/HrPageLayout.vue';
import { authUrl, execute } from '@/api';

export default {
  name: 'OffDaysManagement',
  components: { HrPageLayout },
  data() {
    const y = new Date().getFullYear();
    return {
      loading: false,
      saving: false,
      showForm: false,
      search: '',
      selectedYear: y,
      yearOptions: [y - 1, y, y + 1],
      programs: [],
      departments: [],
      employeeSearch: '',
      employeeResults: [],
      selectedEmployees: [],
      form: this.emptyForm()
    };
  },
  mounted() {
    this.loadDepartments();
    this.loadPrograms();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        programName: '',
        description: '',
        year: new Date().getFullYear(),
        maxDaysPerYear: 1,
        allowHalfDay: false,
        isActive: true,
        departmentIds: []
      };
    },
    async loadDepartments() {
      const data = new FormData();
      data.append('function', 'getDepartments');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.departments = res.data.data?.departments || res.data.data || [];
        }
      } catch (e) {
        console.error(e);
      }
    },
    async loadPrograms() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getOffDayPrograms');
      data.append('year', this.selectedYear);
      data.append('search', this.search);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.programs = res.data.data?.programs || [];
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openForm(program) {
      this.showForm = true;
      this.selectedEmployees = [];
      this.employeeResults = [];
      if (!program) {
        this.form = this.emptyForm();
        this.form.year = this.selectedYear;
        return;
      }
      this.loadProgramDetails(program.id);
    },
    async loadProgramDetails(id) {
      const data = new FormData();
      data.append('function', 'getOffDayProgramDetails');
      data.append('programId', id);
      try {
        const res = await execute(data, authUrl);
        if (!res.data.success) return;
        const p = res.data.data.program;
        const ids = res.data.data.nationalIds || [];
        this.form = {
          id: p.id,
          programName: p.program_name,
          description: p.description || '',
          year: parseInt(p.year, 10),
          maxDaysPerYear: parseFloat(p.max_days_per_year),
          allowHalfDay: p.allow_half_day == 1,
          isActive: p.is_active == 1,
          departmentIds: (res.data.data.departmentIds || []).map(Number)
        };
        this.selectedEmployees = ids.map(nid => ({ nationalId: nid, label: nid }));
      } catch (e) {
        console.error(e);
      }
    },
    closeForm() {
      this.showForm = false;
    },
    async searchEmployees() {
      if (!this.employeeSearch.trim()) return;
      const data = new FormData();
      data.append('function', 'searchEmployees');
      data.append('searchQuery', this.employeeSearch.trim());
      data.append('pageSize', 20);
      try {
        const res = await execute(data, authUrl);
        const list = res.data.data?.employees || [];
        this.employeeResults = (Array.isArray(list) ? list : []).map(e => ({
          ...e,
          fullName: e.fullName || e.name || ''
        }));
      } catch (e) {
        console.error(e);
      }
    },
    addEmployee(e) {
      const nid = e.nationalId || e.national_id;
      if (!nid || this.selectedEmployees.some(x => x.nationalId === nid)) return;
      const label = (e.fullName || e.name || `${e.firstName || ''} ${e.lastName || ''}`.trim()) + ` (${nid})`;
      this.selectedEmployees.push({ nationalId: nid, label });
    },
    removeEmployee(nid) {
      this.selectedEmployees = this.selectedEmployees.filter(e => e.nationalId !== nid);
    },
    async saveProgram() {
      if (!this.form.programName.trim()) {
        alert('Program name is required');
        return;
      }
      if (!this.form.departmentIds.length && !this.selectedEmployees.length) {
        alert('Assign at least one department or employee');
        return;
      }
      this.saving = true;
      const data = new FormData();
      data.append('function', 'saveOffDayProgram');
      if (this.form.id) data.append('programId', this.form.id);
      data.append('programName', this.form.programName.trim());
      data.append('description', this.form.description);
      data.append('year', this.form.year);
      data.append('maxDaysPerYear', this.form.maxDaysPerYear);
      data.append('allowHalfDay', this.form.allowHalfDay ? '1' : '0');
      data.append('isActive', this.form.isActive ? '1' : '0');
      data.append('departmentIds', JSON.stringify(this.form.departmentIds));
      data.append('nationalIds', JSON.stringify(this.selectedEmployees.map(e => e.nationalId)));
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.closeForm();
          this.loadPrograms();
        } else {
          alert(res.data.message || 'Save failed');
        }
      } catch (e) {
        alert('Save failed');
      } finally {
        this.saving = false;
      }
    },
    async toggleProgram(p) {
      const data = new FormData();
      data.append('function', 'toggleOffDayProgram');
      data.append('programId', p.id);
      await execute(data, authUrl);
      this.loadPrograms();
    },
    async deleteProgram(p) {
      if (!confirm(`Delete program "${p.program_name}"?`)) return;
      const data = new FormData();
      data.append('function', 'deleteOffDayProgram');
      data.append('programId', p.id);
      const res = await execute(data, authUrl);
      if (res.data.success) this.loadPrograms();
      else alert(res.data.message || 'Delete failed');
    }
  }
};
</script>
